import { useCallback } from 'react';
import { ConnectionHandler, graphql, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useFlagsService, type FlagConfiguration } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import {
	getAnalyticsTabOrder,
	getStoreEdges,
	getConnection,
} from '@atlassian/jira-horizontal-nav-common/src/common/utils.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	JiraDeleteNavigationItemInput,
	useHideNavigationItemMutation,
	useHideNavigationItemMutation$data,
} from './__generated__/useHideNavigationItemMutation.graphql';
import messages from './messages.tsx';

// exported for testing
export const PACKAGE_NAME = 'jiraNavigationKitHideItemService';
export const HIDE_VIEW = 'hideView';
export const TEAM_NAME = 'Navigation';
export const HIDE_ITEM_EXPERIENCE = { experience: HIDE_VIEW };

const NON_ADMIN_ERROR = 'User is not an admin';

export type HideNavigationItemArgs = {
	id: JiraDeleteNavigationItemInput['id'] | null;
	relayId: string | null;
	onStart: () => void;
	// TODO Remove when cleaning up jira_list_nin_merge_technical_changes
	onCompleted: () => void;
	containerNavigationId: string;
};

const getFlagDetails = (error: MutationError): FlagConfiguration | null => {
	const { message } = error;
	const { statusCode } = error.extensions || {};

	switch (statusCode) {
		case 401:
			return {
				type: 'error',
				title: messages.userNotLoggedInErrorFlagTitle,
				description: messages.userNotLoggedInErrorFlagDescription,
				messageId: 'horizontal-nav-hide-item-service.services.flag.error.user-not-logged-in-error',
				messageType: 'transactional',
			};
		case 403:
			if (message === NON_ADMIN_ERROR) {
				return {
					type: 'error',
					title: messages.userNotAdminErrorFlagTitle,
					description: messages.userNotAdminErrorFlagDescription,
					messageId: 'horizontal-nav-hide-item-service.services.flag.error.user-not-admin-error',
					messageType: 'transactional',
				};
			}
			return null;

		case 404:
			return {
				type: 'error',
				title: messages.viewNotFoundErrorFlagTitle,
				description: messages.viewNotFoundErrorFlagDescription,
				messageId: 'horizontal-nav-hide-item-service.services.flag.error.view-not-found-error',
				messageType: 'transactional',
			};
		default:
			return null;
	}
};

export const useHideNavigationItem = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	const onStartExperience = useExperienceStart({
		...HIDE_ITEM_EXPERIENCE,
		analyticsSource: PACKAGE_NAME,
	});

	const onExperienceSuccess = useExperienceSuccess(HIDE_ITEM_EXPERIENCE);
	const onExperienceFail = useExperienceFail(HIDE_ITEM_EXPERIENCE);

	const [commitMutation] = useMutation<useHideNavigationItemMutation>(graphql`
		mutation useHideNavigationItemMutation($input: JiraDeleteNavigationItemInput!) {
			jira {
				deleteNavigationItem(input: $input) {
					success
					navigationItem
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		}
	`);

	const getAnalyticsTabPosition = useCallback(
		(
			store: RecordSourceSelectorProxy,
			response: useHideNavigationItemMutation$data | null | undefined,
			containerNavigationId: string,
		): number | undefined => {
			if (response?.jira?.deleteNavigationItem == null) return;

			const { success, navigationItem } = response.jira.deleteNavigationItem;
			if (!success || navigationItem == null) {
				return;
			}

			const edges = getStoreEdges(store, containerNavigationId);
			if (!edges) return undefined;

			const index = edges.findIndex((edge) => {
				const node = edge?.getLinkedRecord('node');
				return node?.getValue('id') === navigationItem;
			});
			return index === -1 ? undefined : index;
		},
		[],
	);

	const updater = useCallback(
		(
			store: RecordSourceSelectorProxy,
			response: useHideNavigationItemMutation$data | null | undefined,
			navigationItemRelayId: string | null,
			containerNavigationId: HideNavigationItemArgs['containerNavigationId'],
		) => {
			if (response?.jira?.deleteNavigationItem == null) {
				return;
			}

			const { success, navigationItem } = response.jira.deleteNavigationItem;
			if (!success || navigationItem == null) {
				return;
			}

			const connectionRecord = getConnection(store, containerNavigationId);

			if (!connectionRecord || navigationItemRelayId == null) {
				return;
			}

			ConnectionHandler.deleteNode(connectionRecord, navigationItemRelayId);
		},
		[],
	);

	const onError = useCallback(
		(error: MutationError | Error) => {
			showFlag({
				messageId:
					'horizontal-nav-hide-item-service.services.show-flag.error.hide-navigation-item-default-error',
				messageType: 'transactional',
				type: 'error',
				title: formatMessage(messages.hideNavigationItemDefaultErrorFlagTitle),
				description: formatMessage(messages.hideNavigationItemDefaultErrorFlagDescription),
			});

			fireErrorAnalytics({
				meta: {
					id: HIDE_VIEW,
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			onExperienceFail(PACKAGE_NAME, error);
		},
		[formatMessage, onExperienceFail, showFlag],
	);
	return useCallback(
		({ id, relayId, onStart, onCompleted, containerNavigationId }: HideNavigationItemArgs) => {
			if (id == null) {
				return;
			}

			onStartExperience();
			if (fg('jira_list_nin_merge_technical_changes')) {
				onStart();
			}

			commitMutation({
				variables: {
					input: {
						id,
					},
				},
				optimisticResponse: {
					jira: {
						deleteNavigationItem: {
							success: true,
							navigationItem: id,
							errors: null,
						},
					},
				},
				optimisticUpdater: (
					store: RecordSourceSelectorProxy,
					response: useHideNavigationItemMutation$data | null | undefined,
				) => {
					updater(store, response, relayId, containerNavigationId);
				},
				updater: (
					store: RecordSourceSelectorProxy,
					response: useHideNavigationItemMutation$data | null | undefined,
				) => {
					const initialTabOrder = getAnalyticsTabOrder(store, containerNavigationId);
					const initialTabPosition = getAnalyticsTabPosition(
						store,
						response,
						containerNavigationId,
					);
					updater(store, response, relayId, containerNavigationId);

					if (response?.jira?.deleteNavigationItem?.success) {
						const finalTabOrder = getAnalyticsTabOrder(store, containerNavigationId);
						const item = store.get(String(relayId));
						const itemId = item?.getValue('appId');
						const itemType = String(item?.getValue('typeKey'));
						fireTrackAnalytics(createAnalyticsEvent({}), 'projectNavigationTabItem updated', {
							originalItems: initialTabOrder,
							updatedItems: finalTabOrder,
							itemType: itemType.toLowerCase(),
							itemId: itemId && String(itemId),
							initialTabIndex: initialTabPosition,
							action: 'removed',
						});
					}
				},
				onError,
				onCompleted: (response) => {
					const { errors } = response?.jira?.deleteNavigationItem ?? {};
					if (errors) {
						const error = new MutationError(errors[0]);
						const statusCode = error.statusCode;

						if (statusCode != null) {
							const flagDetails = getFlagDetails(error);
							if (flagDetails) {
								showFlag(flagDetails);
								onExperienceSuccess();
								return;
							}
						}

						onError(error);
						return;
					}

					onExperienceSuccess();
					if (!fg('jira_list_nin_merge_technical_changes')) {
						onCompleted();
					}
				},
			});
		},

		[
			commitMutation,
			createAnalyticsEvent,
			getAnalyticsTabPosition,
			onError,
			onExperienceSuccess,
			onStartExperience,
			showFlag,
			updater,
		],
	);
};
